/**
 * packages/contracts/src/contract.ts
 * -------------------------------------------------------
 * Purpose:
 *   Defines the single source of truth for the HTTP API using
 *   ts-rest + zod. Both server and client import this contract.
 *
 * High-level data flow:
 *   - Server (apps/api) implements these routes and MUST return
 *     responses matching the zod schemas below.
 *   - Web (apps/web) calls the same routes using types inferred
 *     from this contract, so breaking changes fail TypeScript.
 */
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { AnalyticsRangeSchema, AnalyticsSummarySchema, ApiErrorSchema, AuditDetailSchema, AuditListResponseSchema, AuditReviewActivityResponseSchema, AuditReviewStateSchema, AuditListQuerySchema, UpsertAuditReviewStateBodySchema, MeSchema, } from './schemas.js';
/** Contract factory from ts-rest. */
const c = initContract();
/** Standard 200 response schema used for liveness checks. */
const HealthSchema = z.object({
    /** True if the process is healthy. */
    ok: z.literal(true),
    /** ISO timestamp generated by the server. */
    ts: z.string().datetime(),
});
/** Shared error responses included on most endpoints. */
const CommonErrorResponses = {
    409: ApiErrorSchema,
    500: ApiErrorSchema,
};
/**
 * API contract used by both server and client.
 * Note: Better Auth endpoints live under `/api/auth/*` and are not part of this contract.
 */
export const apiContract = c.router({
    health: {
        method: 'GET',
        path: '/api/health',
        responses: {
            200: HealthSchema,
            ...CommonErrorResponses,
        },
        summary: 'Health check',
    },
    me: {
        method: 'GET',
        path: '/api/me',
        responses: {
            200: MeSchema,
            401: ApiErrorSchema,
            ...CommonErrorResponses,
        },
        summary: 'Return the current session user + active org context',
    },
    analyticsSummary: {
        method: 'GET',
        path: '/api/analytics/summary',
        query: z.object({
            /** Optional time range bucket (client-defined, server treats as a hint). */
            range: AnalyticsRangeSchema.optional(),
        }),
        responses: {
            200: AnalyticsSummarySchema,
            401: ApiErrorSchema,
            ...CommonErrorResponses,
        },
        summary: 'Return a small analytics summary for the dashboard',
    },
    adminAuditReviewActivity: {
        method: 'GET',
        path: '/api/admin/audit-review-states',
        query: z.object({
            /** Optional time range bucket used to filter audit_review_states.updated_at. */
            range: AnalyticsRangeSchema.optional(),
        }),
        responses: {
            200: AuditReviewActivityResponseSchema,
            401: ApiErrorSchema,
            403: ApiErrorSchema,
            ...CommonErrorResponses,
        },
        summary: 'Return admin-only review activity derived from audit_review_states',
    },
    auditsList: {
        method: 'GET',
        path: '/api/audits',
        query: AuditListQuerySchema,
        responses: {
            200: AuditListResponseSchema,
            401: ApiErrorSchema,
            403: ApiErrorSchema,
            ...CommonErrorResponses,
        },
        summary: 'List audits for the active org (supports search + basic filters)',
    },
    auditById: {
        method: 'GET',
        path: '/api/audits/:auditId',
        responses: {
            200: AuditDetailSchema,
            401: ApiErrorSchema,
            403: ApiErrorSchema,
            404: ApiErrorSchema,
            ...CommonErrorResponses,
        },
        summary: 'Fetch full details for a single audit',
    },
    auditReviewStateUpsert: {
        method: 'PUT',
        path: '/api/audits/:auditId/review-state',
        body: UpsertAuditReviewStateBodySchema,
        responses: {
            200: AuditReviewStateSchema,
            400: ApiErrorSchema,
            401: ApiErrorSchema,
            403: ApiErrorSchema,
            404: ApiErrorSchema,
            ...CommonErrorResponses,
        },
        summary: 'Upsert local manual review state for a single audit',
    },
});
